import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useState, useEffect } from "react";

import { useT } from "src/internationalization";

export type SimpleCompany = {
  name: string | null;
  address: string | null;
  siren: string | null;
};

export type LatLng = { lat: number; lng: number };

type UseResolvedLocationInput = {
  address?: string;
  company?: SimpleCompany | null;
  fallbackCenter?: LatLng;
};

export const useLocation = ({
  address,
  company,
  fallbackCenter = { lat: 48.8566, lng: 2.3522 },
}: UseResolvedLocationInput) => {
  const t = useT();
  const geocodingLibrary = useMapsLibrary("geocoding");
  const placesLibrary = useMapsLibrary("places");

  const [placeLocation, setPlaceLocation] = useState<LatLng | null>(null);
  const [placeDetails, setPlaceDetails] = useState<google.maps.places.PlaceResult | null>(null);
  const [geoCoderDetails, setGeoCoderDetails] = useState<google.maps.GeocoderResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const resolvedAddress = address ?? company?.address ?? null;

  const companyName = company?.name ?? null;

  useEffect(() => {
    if (!resolvedAddress || !geocodingLibrary) {
      setLoading(false);
      if (!resolvedAddress) setError(t("page.alertDetails.googleMaps.addressRequired"));
      return;
    }

    const geocoder = new geocodingLibrary.Geocoder();
    geocoder.geocode({ address: resolvedAddress }, (results, status) => {
      setLoading(false);
      if (status === "OK" && results?.[0]) {
        setGeoCoderDetails(results[0]);
        setError(null);
      } else {
        setError(t("widget.googleMap.geocode.error"));
      }
    });
  }, [resolvedAddress, geocodingLibrary, t]);

  useEffect(() => {
    if (!companyName || !placesLibrary || !geoCoderDetails) return;

    const service = new placesLibrary.PlacesService(document.createElement("div"));

    service.findPlaceFromQuery(
      { query: companyName, fields: ["name", "formatted_address", "geometry", "place_id"] },
      (results, status) => {
        if (status === "OK" && results?.[0]?.place_id) {
          const placeId = results[0].place_id;
          service.getDetails({ placeId, fields: ["all"] }, (place, status) => {
            if (status === "OK" && place) {
              const location = place.geometry?.location ?? place.geometry?.viewport?.getCenter();
              if (location) {
                setPlaceLocation({ lat: location.lat(), lng: location.lng() });
              }
              setPlaceDetails(place);
              setError(null);
            } else {
              setError(t("widget.googleMap.place.error"));
            }
          });
        } else {
          setError(t("widget.googleMap.place.error", { status }));
        }
      }
    );
  }, [companyName, placesLibrary, geoCoderDetails, t]);

  return {
    center: placeLocation ?? fallbackCenter,
    placeLocation,
    geocodedLocation: geoCoderDetails
      ? {
          lat: geoCoderDetails.geometry.viewport.getCenter().lat(),
          lng: geoCoderDetails.geometry.viewport.getCenter().lng(),
        }
      : null,
    placeDetails,
    geoCoderDetails,
    error,
    loading,
  };
};
